/**
 * Extraction History Component
 * View and manage extraction history - load or clear extractions
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
} from '@mui/material';
import {
  CloudDownload as LoadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as PdfIcon,
  TableChart as ExcelIcon,
  Send as SendIcon,
  Note as NoteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import * as authApi from '../services/authApi';
import { useNavigate } from 'react-router-dom';
import { useBOM } from '../contexts/BOMContext';
import * as bomApi from '../services/bomApi';
import { ExtractionHistoryItem } from '../services/bomApi';

const ExtractionHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const {
    setUploadedPdf,
    setUploadedXls,
    setLoading,
    showToast,
    clearExtractedItems,
    addExtractedItems,
    setComponents,
    setManufacturers,
    setMetadata,
    setFeatures,
    setDimensions,
    setTotalPages,
    setCurrentPage,
    uploadedPdfFilename,
  } = useBOM();

  const [history, setHistory] = useState<ExtractionHistoryItem[]>([]);
  const [loading, setLoadingState] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [sendEmailDialog, setSendEmailDialog] = useState<{ open: boolean; item: ExtractionHistoryItem | null }>({ open: false, item: null });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [notesDialog, setNotesDialog] = useState<{ open: boolean; item: ExtractionHistoryItem | null }>({ open: false, item: null });
  const [editingNotes, setEditingNotes] = useState<string>('');

  const loadHistory = async () => {
    setLoadingState(true);
    try {
      const data = await bomApi.getExtractionHistory();
      console.log('[ExtractionHistory] Loaded history:', data.history);
      console.log('[ExtractionHistory] First item notes:', data.history[0]?.notes);
      setHistory(data.history);
    } catch (error: any) {
      showToast('error', `Failed to load extraction history: ${error.message}`);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    loadHistory();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    if (!token) return;
    try {
      const result = await authApi.listUsers(token);
      setAvailableUsers(result.users);
    } catch (error: any) {
      console.error('Failed to load users:', error);
    }
  };

  const handleLoadExtraction = async (item: ExtractionHistoryItem) => {
    setLoading(true, 'Loading extraction...');
    try {
      const loadedData = await bomApi.loadExtraction(item.job_id);

      // Set PDF and Excel files
      setUploadedPdf(item.pdf_filename, item.pdf_url);
      if (item.xls_filename && item.xls_url) {
        setUploadedXls(item.xls_filename, item.xls_url);
      }

      // Load all extraction data
      clearExtractedItems();
      addExtractedItems(loadedData.items);
      
      // Derive components and manufacturers from loaded items
      const manufacturerMap = new Map<string, {
        manufacturer: string;
        component_count: number;
        components: string[];
        pages: number[];
      }>();
      
      const componentMap = new Map<string, {
        type: string;
        count: number;
        examples: string[];
        pages: number[];
      }>();
      
      loadedData.items.forEach((item: any) => {
        // Process manufacturer
        const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
        if (!manufacturerMap.has(mfr)) {
          manufacturerMap.set(mfr, {
            manufacturer: mfr,
            component_count: 0,
            components: [],
            pages: [],
          });
        }
        const mfrData = manufacturerMap.get(mfr)!;
        const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
        mfrData.component_count += qty;
        const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
        if (!mfrData.components.includes(ref)) {
          mfrData.components.push(ref);
        }
        const page = item.page || (item.pages && item.pages[0]) || 1;
        if (page && !mfrData.pages.includes(page)) {
          mfrData.pages.push(page);
        }
        
        // Process component type
        const compType = item.component_type || item.type || 'Unknown';
        if (!componentMap.has(compType)) {
          componentMap.set(compType, {
            type: compType,
            count: 0,
            examples: [],
            pages: [],
          });
        }
        const compData = componentMap.get(compType)!;
        compData.count += qty;
        if (!compData.examples.includes(ref)) {
          compData.examples.push(ref);
        }
        if (page && !compData.pages.includes(page)) {
          compData.pages.push(page);
        }
      });
      
      const derivedManufacturers = Array.from(manufacturerMap.values())
        .sort((a, b) => b.component_count - a.component_count);
      const derivedComponents = Array.from(componentMap.values())
        .sort((a, b) => b.count - a.count);
      
      setComponents(derivedComponents);
      setManufacturers(derivedManufacturers);
      setMetadata(loadedData.metadata || null);
      setFeatures(loadedData.features || []);
      setDimensions(loadedData.dimensions || []);
      setTotalPages(loadedData.total_pages || 1);
      setCurrentPage(2); // Mark as complete

      showToast('success', `Loaded extraction with ${loadedData.items.length} items`);
      navigate('/training');
    } catch (error: any) {
      showToast('error', `Failed to load extraction: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedJobId) return;

    setLoading(true, 'Clearing extraction...');
    try {
      // Find the extraction being deleted to check if it matches current PDF
      const extractionToDelete = history.find(item => item.job_id === selectedJobId);
      
      await bomApi.clearExtraction(selectedJobId);
      showToast('success', 'Extraction cleared successfully');
      
      // If the deleted extraction matches the currently loaded PDF, clear the extraction state
      // This will enable the extraction button again
      if (extractionToDelete && uploadedPdfFilename === extractionToDelete.pdf_filename) {
        clearExtractedItems();
        setComponents([]);
        setManufacturers([]);
        setMetadata(null);
        setFeatures([]);
        setDimensions([]);
        setTotalPages(null);
        setCurrentPage(1);
        showToast('info', 'Extraction cleared. You can now extract again.');
      }
      
      setDeleteDialogOpen(false);
      setSelectedJobId(null);
      loadHistory(); // Reload history
    } catch (error: any) {
      showToast('error', `Failed to clear extraction: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const extractOriginalFilename = (filename: string): string => {
    /**
     * Extract original filename by removing UUID prefix if present.
     * UUID format: 32 hex characters followed by underscore.
     * Example: 'a3fa73575b844ca0acdf8b33c9b3634d_101034024 REV 19.pdf' 
     * Returns: '101034024 REV 19.pdf'
     */
    if (!filename) return filename;
    if (filename.includes('_')) {
      const parts = filename.split('_', 2);
      // Check if first part is a 32-character hex string (UUID without dashes)
      if (parts.length >= 2 && parts[0].length === 32 && /^[0-9a-f]{32}$/i.test(parts[0])) {
        return parts.slice(1).join('_'); // Join remaining parts in case filename has underscores
      }
    }
    return filename;
  };

  const handleExportExcel = async (item: ExtractionHistoryItem) => {
    try {
      const loadedData = await bomApi.loadExtraction(item.job_id);
      const allRows: any[] = [];

      (loadedData.items || []).forEach((ext: any) => {
        const pagesArr = ext.pages || (ext.page ? [ext.page] : []);
        const pagesStr = pagesArr.join(', ');
        allRows.push({
          'Tag No': ext.reference || '',
          'Type/Category': ext.component_type || ext.type || '',
          'Pages': pagesStr,
          'Description': ext.description || ext.raw_line || '',
          'Part Number': ext.part_number || ext.pn_guess || '',
          'Manufacturer': ext.manufacturer || ext.manufacturer_suggestion || '',
          'Qty': Number(ext.qty || ext.quantity || 1),
          'Confidence %': Math.round((ext.confidence || 0) * 100),
          'Verification': ext.verification_status || 'unverified',
        });
      });

      if (allRows.length === 0) {
        showToast('warning', 'No data to export');
        return;
      }

      const headers = Object.keys(allRows[0]);
      const csvContent = [
        headers.map(h => `"${h}"`).join(','),
        ...allRows.map(row => headers.map(h => {
          const val = row[h] ?? '';
          return typeof val === 'number' ? val : `"${String(val).replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cleanName = extractOriginalFilename(item.pdf_filename).replace(/\.pdf$/i, '');
      a.href = url;
      a.download = `extraction_${cleanName}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      showToast('success', `Exported ${allRows.length} items`);
    } catch (error: any) {
      showToast('error', `Export failed: ${error.message}`);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Extraction History</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadHistory}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* History Table */}
      {loading && history.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : history.length === 0 ? (
        <Alert severity="info">No extraction history found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>PDF File</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, width: '40%' }}>Notes</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Model</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Items</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Completed</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow key={item.job_id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PdfIcon fontSize="small" />
                      <Typography variant="body2">{extractOriginalFilename(item.pdf_filename)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '40%' }}>
                    <Box 
                      sx={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        padding: 1,
                        borderRadius: 1,
                      }}
                      onClick={() => {
                        setNotesDialog({ open: true, item });
                        setEditingNotes(item.notes || '');
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        color={item.notes ? 'text.primary' : 'text.secondary'}
                        sx={{ fontStyle: item.notes ? 'normal' : 'italic' }}
                        title={item.notes || 'Click to add notes'}
                      >
                        {item.notes || 'Click to add notes'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.model} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.total_candidates || 0} items
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.completed_at ? formatDate(item.completed_at) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleLoadExtraction(item)}
                        title="Load extraction"
                      >
                        <LoadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleExportExcel(item)}
                        title="Export extraction to CSV"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => {
                          setSendEmailDialog({ open: true, item });
                          setSelectedUsers([]);
                        }}
                        title="Send extraction summary via email"
                      >
                        <SendIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(item.job_id)}
                        title="Clear extraction"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Clear Extraction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear this extraction? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog
        open={notesDialog.open}
        onClose={() => setNotesDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Notes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add or edit notes for <strong>{notesDialog.item ? extractOriginalFilename(notesDialog.item.pdf_filename) : ''}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={editingNotes}
            onChange={(e) => setEditingNotes(e.target.value)}
            placeholder="Enter notes about this extraction..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialog({ open: false, item: null })}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!notesDialog.item) return;
              
              try {
                await bomApi.updateJobNotes(notesDialog.item.pdf_filename, editingNotes);
                showToast('success', 'Notes updated successfully');
                
                // Update the history state to reflect the change
                setHistory(prevHistory => 
                  prevHistory.map(historyItem => 
                    historyItem.job_id === notesDialog.item!.job_id
                      ? { ...historyItem, notes: editingNotes }
                      : historyItem
                  )
                );
                
                setNotesDialog({ open: false, item: null });
              } catch (error: any) {
                showToast('error', `Failed to update notes: ${error.message}`);
              }
            }}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog 
        open={sendEmailDialog.open} 
        onClose={() => !isSendingEmail && setSendEmailDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Extraction Summary</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Send extraction summary for <strong>{sendEmailDialog.item?.pdf_filename || ''}</strong> to selected users.
            The email will include extraction details, notes, and any rules or missing items.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="select-users-label">Select Users</InputLabel>
            <Select
              labelId="select-users-label"
              multiple
              value={selectedUsers}
              onChange={(e) => setSelectedUsers(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Select Users" />}
              renderValue={(selected) => {
                const selectedNames = selected.map(email => {
                  const user = availableUsers.find(u => u.email === email);
                  return user?.full_name || user?.username || email;
                });
                return selectedNames.join(', ');
              }}
            >
              {availableUsers.map((user) => (
                <MenuItem key={user.id} value={user.email}>
                  <Checkbox checked={selectedUsers.indexOf(user.email) > -1} />
                  <ListItemText 
                    primary={user.full_name || user.username} 
                    secondary={user.email}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEmailDialog({ open: false, item: null })} disabled={isSendingEmail}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!sendEmailDialog.item || selectedUsers.length === 0) {
                showToast('error', 'Please select at least one user');
                return;
              }
              
              setIsSendingEmail(true);
              try {
                await bomApi.sendExtractionSummary(sendEmailDialog.item.job_id, selectedUsers);
                showToast('success', `Extraction summary sent to ${selectedUsers.length} user(s)`);
                setSendEmailDialog({ open: false, item: null });
                setSelectedUsers([]);
              } catch (error: any) {
                showToast('error', `Failed to send email: ${error.message}`);
              } finally {
                setIsSendingEmail(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={isSendingEmail || selectedUsers.length === 0}
            startIcon={<SendIcon />}
          >
            {isSendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExtractionHistory;

